import { useSyncExternalStore } from "react";
import { Icons } from "./Icons.js";

/**
 * Bandeau de génération de résumé. La génération passe par le modèle distant : elle prend plusieurs
 * secondes, pendant lesquelles rien ne l'indiquait — la fenêtre restait figée et on ne savait pas si
 * quelque chose se passait. Le bandeau annonce donc le travail en cours, puis sa fin ; **appuyer sur
 * le bandeau de fin ouvre le résumé produit**, sans avoir à le chercher dans les comptes rendus.
 *
 * L'état vit dans un petit store externe (même approche que UpdateBanner) : la génération est
 * déclenchée depuis plusieurs écrans (fenêtre d'une note, barre des comptes rendus) alors que le
 * bandeau, lui, est rendu une seule fois au niveau de l'application.
 */

type State =
  | { phase: "idle" }
  | { phase: "running"; label: string }
  | { phase: "done"; label: string; summaryId: string }
  | { phase: "error"; message: string };

let state: State = { phase: "idle" };
const listeners = new Set<() => void>();
function emit(): void {
  listeners.forEach((l) => l());
}

/** Génération lancée : `label` décrit la cible (« Résumé du jour », le titre de la note…). */
export function summaryStarted(label: string): void {
  state = { phase: "running", label };
  emit();
}
/** Génération terminée : le bandeau devient cliquable et ouvre le résumé produit. */
export function summaryDone(label: string, summaryId: string): void {
  state = { phase: "done", label, summaryId };
  emit();
}
export function summaryFailed(message: string): void {
  state = { phase: "error", message };
  emit();
}
export function summaryDismiss(): void {
  state = { phase: "idle" };
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot(): State {
  return state;
}

export function SummaryToast({ onOpen }: { onOpen: (summaryId: string, label: string) => void }) {
  const s = useSyncExternalStore(subscribe, getSnapshot);
  if (s.phase === "idle") return null;

  if (s.phase === "running") {
    return (
      <div className="sumtoast" role="status">
        <span className="spinner spinner-sm" />
        <span className="sumtoast-t">{s.label} — génération en cours…</span>
      </div>
    );
  }

  if (s.phase === "error") {
    return (
      <div className="sumtoast err" role="status">
        <span className="sumtoast-t">{s.message}</span>
        <button className="sumtoast-x" onClick={summaryDismiss} aria-label="Fermer">
          <Icons.Close size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="sumtoast ok" role="status">
      <button
        className="sumtoast-open"
        onClick={() => {
          onOpen(s.summaryId, s.label);
          summaryDismiss();
        }}
      >
        <Icons.Doc size={16} />
        <span className="sumtoast-t">{s.label} prêt — appuyez pour l'ouvrir</span>
      </button>
      <button className="sumtoast-x" onClick={summaryDismiss} aria-label="Fermer">
        <Icons.Close size={15} />
      </button>
    </div>
  );
}
